import { BrandConfigResponse } from './getInitialState';

interface BannerDto {
  ctaKey: string;
  ctaUrl: string;
  titleKey: string;
  subtitleKey: string;
}

interface ThemeDto {
  headerColor: string;
  headerButtonColor: string;
  headerButtonTextColor: string;
  headerButtonHoverColor: string;

  primaryColor: string;
  primaryHoverColor: string;
  primarySoftColor: string;
  primaryTextColor: string;

  favicon: string;
  logoPrimaryUrl: string;
  logoSecondaryUrl: string;
  mainGradient: string;
}

interface FreeFlightConditionsDto {
  adults: number;
  departureCountry: string;
  voucherAmount: number;
}

interface PoliciesDto {
  privacy: string;
  cookies: string;
  termsConditions: string;
}

interface BrandDto {
  pageTitle: string;
  businessProfileIdH?: string;
  businessProfileIdDP?: string;
  businessProfileIdF?: string;
  businessProfileIdFF?: string;
  businessProfileIdHGH?: string;
  isTopHotelProductType: boolean;
  productTypesOrder: string[];
  phoneNumber?: string;
  phonePrivacyPolicyUrl?: string;
  brandName: string;
  businessClient: string;
  brandBase: string;
  brandOrigin: string;
  hasSSO: boolean;
  currency: string;
  freeFlightConditions: FreeFlightConditionsDto;
  policies: PoliciesDto;
  isScalapayClub: boolean;
  budgetRestriction?: 'high' | 'low';
}

interface LandingContentDto {
  hero: {
    title: string;
    subtitle: string;
    backgroundUrl: string;
    promoImageMobile?: string;
    promoImageDesktop?: string;
  };
  productExplanation: {
    title: string;
    subtitle: string;
    description: string;
  };

  productSelector: {
    extraSelector: {
      textKey: string;
      url: string;
    };
    showSelector: boolean;
  };
}

interface OffersDto {
  dealsSubtitleKey: string;
  dealsTitleKey: string;
  favouriteDestinationsSubtitleKey: string;
  favouriteDestinationsTitleKey: string;
  tripTypeSubtitleKey: string;
  tripTypeTitleKey: string;
  showSearchOnDeals: boolean;
  dealsListTitleKey: string;
  dealsListSubtitleKey: string;
}

interface PromoCampaignDto {
  banner: {
    dp: BannerDto;
    h: BannerDto;
    f: BannerDto;
  };
}

interface FooterDto {
  embebed?: {
    scriptUrl?: string;
  };
  custom?: {
    legalText: string;
  };
}

interface DestinationProposalsDto {
  id: number;
  name: string;
  subtitle: any;
  type: string;
  code: string;
  title: string;
  imageUrl: string;
}

export type BrandConfig = {
  id: string;
  domain: string;
  environment: string;
  language: string;
  country: string;
  theme: ThemeDto;
  brand: BrandDto;
  landingContent?: LandingContentDto;
  offers?: OffersDto;
  promoCampaign?: PromoCampaignDto;

  footer?: FooterDto;
  destinationProposals_DP?: DestinationProposalsDto[];
  destinationProposals_H?: DestinationProposalsDto[];
  destinationProposals_F?: DestinationProposalsDto[];
};

export const configMapper = (info: BrandConfigResponse): BrandConfig => {
  const { id, domain, brandConfig } = info;

  const getBPByProduct = (
    productType: 'DP' | 'F' | 'H' | 'FF' | 'HGH'
  ): string => {
    const product = brandConfig.products.find(
      (p) => p.productType === productType
    );

    return product?.businessProfileId || undefined;
  };

  const mapInfo = {
    id: id,
    domain: domain,
    language: brandConfig.brandDetails.language,
    country: brandConfig.brandDetails.country,
    environment: brandConfig.environment,
    theme: {
      ...brandConfig.theme.data.attributes,
      favicon: brandConfig.theme.data.attributes.favicon.data.attributes.url,
      logoPrimaryUrl:
        brandConfig.theme.data.attributes.logoPrimaryUrl.data.attributes.url,
      logoSecondaryUrl:
        brandConfig.theme.data.attributes.logoSecondaryUrl.data.attributes.url,
    },
    landingContent: {
      hero: {
        title: brandConfig.landingContent.hero.title,
        subtitle: brandConfig.landingContent.hero.subtitle,
        backgroundUrl:
          brandConfig.landingContent.hero.hero_background.data.attributes.url,
        promoImageMobile:
          brandConfig.landingContent.hero?.promo_img_mobile?.data?.attributes
            ?.url,
        promoImageDesktop:
          brandConfig.landingContent.hero?.promo_img_desktop?.data?.attributes
            ?.url,
      },
      productExplanation: {
        title: brandConfig.landingContent.productExplanation.title,
        subtitle: brandConfig.landingContent.productExplanation.subtitle,
        description: brandConfig.landingContent.productExplanation.description,
      },

      productSelector: {
        extraSelector: brandConfig.landingContent.extraSelector || undefined,
        showSelector: brandConfig.landingContent.showInSelector,
      },
    },
    offers: brandConfig?.offers,
    brand: {
      pageTitle: brandConfig.brandDetails.pageTitle,
      businessProfileIdH: getBPByProduct('H'),
      businessProfileIdDP: getBPByProduct('DP'),
      businessProfileIdF: getBPByProduct('F'),
      businessProfileIdFF: getBPByProduct('FF'),
      businessProfileIdHGH: getBPByProduct('HGH'),
      isTopHotelProductType: brandConfig.brandDetails.isTopHotel || false,
      productTypesOrder: brandConfig.products.map((item) => item.productType),
      phoneNumber: brandConfig.brandDetails.phoneNumber,
      phonePrivacyPolicyUrl: brandConfig.brandDetails.phonePrivacyPolicyUrl,
      brandName: brandConfig.brandName,
      businessClient: brandConfig.businessClient,
      brandBase: brandConfig.brandDetails.brandBase,
      brandOrigin: brandConfig.brandDetails.brandOrigin,
      hasSSO: brandConfig.hasTokenAuthentication,
      freeFlightConditions: brandConfig.brandDetails.freeFlightConditions,
      currency: brandConfig.brandDetails.currency || 'EUR',
      budgetRestriction: brandConfig.budgetRestriction,
      policies: {
        privacy: brandConfig.brandDetails?.policy?.data?.attributes?.privacy,
        cookies: brandConfig.brandDetails?.policy?.data?.attributes?.cookies,
        termsConditions:
          brandConfig.brandDetails?.policy?.data?.attributes
            ?.termsAndConditions,
      },
      isScalapayClub: brandConfig?.offers?.isScalapayClub || false,
    },
    promoCampaign: {
      banner: {
        dp: {
          ctaKey: brandConfig.promo_campaign?.data?.attributes.bannerDP?.ctaKey,
          ctaUrl: brandConfig.promo_campaign?.data?.attributes.bannerDP?.ctaUrl,
          titleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerDP?.titleKey,
          subtitleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerDP?.subtitleKey,
        },
        h: {
          ctaKey: brandConfig.promo_campaign?.data?.attributes.bannerH?.ctaKey,
          ctaUrl: brandConfig.promo_campaign?.data?.attributes.bannerH?.ctaUrl,
          titleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerH?.titleKey,
          subtitleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerH?.subtitleKey,
        },
        f: {
          ctaKey: brandConfig.promo_campaign?.data?.attributes.bannerF?.ctaKey,
          ctaUrl: brandConfig.promo_campaign?.data?.attributes.bannerF?.ctaUrl,
          titleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerF?.titleKey,
          subtitleKey:
            brandConfig.promo_campaign?.data?.attributes.bannerF?.subtitleKey,
        },
      },
    },
    footer: {
      custom: {
        legalText: brandConfig.brandDetails.footer.legalTextKey,
      },
      embebed: {
        scriptUrl: brandConfig.brandDetails.footer.externalFooter,
      },
    },
    destinationProposals_DP:
      brandConfig.destination_proposal_dp?.data?.attributes?.destinationProposals.map(
        (item) => ({
          id: item.id,
          name: item.name,
          subtitle: item.subtitle,
          type: item.type,
          code: item.code,
          title: item.title,
          imageUrl: item.imageUrl.data?.attributes.url,
        })
      ),
    destinationProposals_H:
      brandConfig.destination_proposal_h?.data?.attributes?.destinationProposals.map(
        (item) => ({
          id: item.id,
          name: item.name,
          subtitle: item.subtitle,
          type: item.type,
          code: item.code,
          title: item.title,
          imageUrl: item.imageUrl.data?.attributes.url,
        })
      ),
    destinationProposals_F:
      brandConfig.destination_proposal_f?.data?.attributes?.destinationProposals.map(
        (item) => ({
          id: item.id,
          name: item.name,
          subtitle: item.subtitle,
          type: item.type,
          code: item.code,
          title: item.title,
          imageUrl: item.imageUrl.data?.attributes.url,
        })
      ),
  };

  return mapInfo;
};
